import moment from 'moment'
import type { View } from 'react-big-calendar'

export const CALENDAR_BUFFER_DAYS = 60

/** Marker eventType for events imported from a connected Google Calendar. */
export const GOOGLE_CALENDAR_EVENT_TYPE = '_google-calendar_'

/**
 * react-big-calendar positions events on the time grid by reading `getHours()`
 * on the raw Date object — which uses the *browser's* local timezone. When the
 * company timezone differs from the browser timezone, an event stored at
 * 09:00 company-time renders at the offset hour locally (e.g. 11:00).
 *
 * This helper returns a Date whose *local* fields (year/month/day/hour/min)
 * match the company-timezone reading of the input. Pass these shifted Dates
 * into BigCalendar so events render at their company-time slot regardless of
 * where the user's browser happens to be.
 */
export function toCalendarLocalDate(input: Date | string | null | undefined, timezone: string): Date | null {
  if (!input) return null
  const m = moment.tz(input, timezone)
  return new Date(m.year(), m.month(), m.date(), m.hours(), m.minutes(), m.seconds(), m.milliseconds())
}

/**
 * Inverse of {@link toCalendarLocalDate}. The input's local fields
 * (year/month/day/hour/min) are interpreted as a *company-timezone* wall clock
 * and converted to the absolute UTC instant they represent.
 *
 * Use this when capturing input from the calendar grid (slot clicks,
 * drag/resize): react-big-calendar reports times via `getHours()` etc., which
 * read browser-local fields — but because we shifted events for display, those
 * fields actually represent company-time. Without this inverse step, a 9am
 * click would save as 9am-browser-local instead of 9am-company-time, and the
 * event would jump to a different hour after the next refetch.
 */
export function fromCalendarLocalDate(input: Date, timezone: string): Date {
  return moment
    .tz(
      {
        year: input.getFullYear(),
        month: input.getMonth(),
        date: input.getDate(),
        hour: input.getHours(),
        minute: input.getMinutes(),
        second: input.getSeconds(),
        millisecond: input.getMilliseconds()
      },
      timezone
    )
    .toDate()
}

/** Returns the start/end of the visible range for a given date and calendar view. */
export function getViewRange(d: Date, v: View): { start: Date; end: Date } {
  const start = new Date(d)
  const end = new Date(d)
  switch (v) {
    case 'week': {
      const dow = start.getDay()
      start.setDate(start.getDate() - dow)
      start.setHours(0, 0, 0, 0)
      end.setDate(end.getDate() + (6 - dow))
      end.setHours(23, 59, 59, 999)
      break
    }
    case 'day':
      start.setHours(0, 0, 0, 0)
      end.setHours(23, 59, 59, 999)
      break
    default: // month
      start.setDate(1)
      start.setHours(0, 0, 0, 0)
      end.setMonth(end.getMonth() + 1, 0)
      end.setHours(23, 59, 59, 999)
  }
  return { start, end }
}

export const handleCalendarNavigate = (date: Date, action: string, timeSpan: 'day' | 'week' | 'month') => {
  switch (action) {
    case 'PREV':
      return moment(date).subtract(1, timeSpan).toDate()
    case 'NEXT':
      return moment(date).add(1, timeSpan).toDate()
    default:
      return date
  }
}

export const getMapTitle = (date: Date, view: 'day' | 'week' | 'month' = 'month') => {
  const m = moment(date)
  if (view === 'day') {
    return m.format('dddd MMMM D, YYYY')
  }
  if (view === 'week') {
    const start = m.clone().startOf('week')
    const end = m.clone().endOf('week')
    if (start.month() === end.month()) {
      return `${start.format('MMMM DD')} - ${end.format('DD')}`
    } else {
      return `${start.format('MMMM DD')} - ${end.format('MMMM DD')}`
    }
  }
  return m.format('MMMM YYYY')
}

export function getEventTimes(date: Date, view: View, timezone: string) {
  // `date` is the slot click time. With `moment.tz.setDefault(timezone)` set
  // by Calendar.tsx, react-big-calendar constructs slot boundaries via the
  // moment localizer in company timezone, so this is already the real UTC
  // instant of the clicked company-tz slot — use it directly.
  if (view === 'month') {
    const nextHour = moment().tz(timezone).add(1, 'hour').startOf('hour')
    const m = moment.tz(date, timezone).set({
      hour: nextHour.hour(),
      minute: 0,
      second: 0,
      millisecond: 0
    })
    return {
      start: m.toDate(),
      end: m.clone().add(1, 'hour').toDate()
    }
  }
  return {
    start: date,
    end: moment(date).add(1, 'hour').toDate()
  }
}
